/**
 * Load More pagination with cross-browser back-navigation restoration.
 *
 * Strategy:
 *  1. history.scrollRestoration = 'manual' is set as the very first line so
 *     Safari cannot fire its scroll-restore event before we inject content.
 *  2. Extra product HTML + scroll position are stored in localStorage after
 *     each Load More click and again (with the exact scroll Y) when the
 *     customer taps a product card.
 *  3. restoreState() runs synchronously while the defer script executes
 *     (after DOM is parsed, before DOMContentLoaded) — products are in the
 *     DOM before any scroll event can fire.
 *  4. scrollTo() is called manually on DOMContentLoaded once layout is ready.
 *  5. If localStorage has no state for this URL, scrollRestoration is reset
 *     to 'auto' so the browser handles scroll normally on non-load-more pages.
 */
(function () {
  'use strict';

  // Must be the first statement — blocks Safari from scrolling before we inject.
  history.scrollRestoration = 'manual';

  // ── localStorage helpers ───────────────────────────────────────────────────


  function stateKey() {
    // Only use origin + pathname + search
    return 'lm:' + location.origin + location.pathname + location.search;
  }

  // Remove all keys for this collection (origin + pathname)
  function clearCollectionStates() {
    var prefix = 'lm:' + location.origin + location.pathname;
    try {
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
          // After removing, localStorage shrinks, so adjust index
          i--;
        }
      }
    } catch (_) {}
  }

  function saveState(scrollY) {
    var grid = document.querySelector('#product-grid .product-grid') ||
               document.getElementById('product-grid');
    var btn  = document.querySelector('.load-more__button');
    if (!grid || !btn) return;

    var pageSize = parseInt(btn.dataset.pageSize) || 0;
    if (!pageSize) return; // can't slice correctly without page size

    var allItems   = Array.from(grid.querySelectorAll(':scope > .grid__item'));
    var extraItems = allItems.slice(pageSize); // everything beyond server-rendered page 1

    // Always clear all states for this collection before saving
    clearCollectionStates();

    if (!extraItems.length) {
      try { localStorage.removeItem(stateKey()); } catch (_) {}
      return;
    }

    try {
      localStorage.setItem(stateKey(), JSON.stringify({
        html:        extraItems.map(function (el) { return el.outerHTML; }).join(''),
        currentPage: parseInt(btn.dataset.currentPage) || 1,
        nextUrl:     btn.dataset.nextUrl    || '',
        totalItems:  btn.dataset.totalItems || '',
        pageSize:    pageSize,
        scrollY:     Math.round(scrollY) || 0,
        ts:          Date.now()
      }));
    } catch (_) {
      // QuotaExceededError — graceful degradation, Load More still works
    }
  }

  // Injects stored HTML synchronously and returns the saved scroll Y.
  function restoreState() {
    try {
      var raw = localStorage.getItem(stateKey());
      if (!raw) return 0;

      var state = JSON.parse(raw);
      if (Date.now() - state.ts > 1800000) { // 30-minute expiry
        localStorage.removeItem(stateKey());
        return 0;
      }

      var grid = document.querySelector('#product-grid .product-grid') ||
                 document.getElementById('product-grid');
      if (!grid) return 0;

      // Synchronous — products are in the DOM before DOMContentLoaded fires.
      grid.insertAdjacentHTML('beforeend', state.html);

      // Restore button state so Load More can continue from the right page.
      var btn = document.querySelector('.load-more__button');
      if (btn) {
        btn.dataset.currentPage = state.currentPage;
        btn.dataset.nextUrl     = state.nextUrl;
        if (state.totalItems) btn.dataset.totalItems = state.totalItems;
        if (state.pageSize)   btn.dataset.pageSize   = state.pageSize;
      }

      return state.scrollY || 0;
    } catch (_) {
      return 0;
    }
  }

  // ── Restore HTML synchronously, then decide how to handle scroll ───────────

  var targetScrollY = restoreState();

  if (targetScrollY > 0) {
    // We have stored content — scroll manually after DOMContentLoaded so
    // CSS aspect-ratio placeholders are applied and layout heights are stable.
    // Double rAF ensures the browser has committed the layout before scrolling.
    function doScroll() {
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          window.scrollTo(0, targetScrollY);
        });
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', doScroll);
    } else {
      doScroll();
    }
  } else {
    // Nothing restored — hand scroll control back to the browser so normal
    // pages (no Load More clicked) behave exactly as before.
    history.scrollRestoration = 'auto';
  }

  // ── Save state + scroll position when tapping a product card ──────────────

  function attachSaveListeners() {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    // Capture phase fires before the browser starts navigating.
    grid.addEventListener('click', function (e) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.target.closest('a[href*="/products/"]')) {
        saveState(window.scrollY); // capture exact scroll at click time
      }
    }, { capture: true });
  }

  // ── Core fetch helper (shared by Load More button) ─────────────────────────

  async function fetchAndAppend(btn) {
    var url = btn.dataset.nextUrl;
    if (!url) return false;

    var resp = await fetch(url);
    if (!resp.ok) throw new Error(resp.status);
    var doc = new DOMParser().parseFromString(await resp.text(), 'text/html');

    var targetGrid =
      document.querySelector('#product-grid .product-grid') ||
      document.getElementById('product-grid');
    var sourceList =
      doc.querySelector('#product-grid.product-grid') ||
      doc.querySelector('#product-grid .product-grid');

    var items = sourceList ? sourceList.querySelectorAll(':scope > .grid__item') : [];
    if (targetGrid && items.length) {
      items.forEach(function (item) { targetGrid.appendChild(item); });
    }

    btn.dataset.currentPage = (parseInt(btn.dataset.currentPage) || 1) + 1;
    var nextBtn = doc.querySelector('.load-more__button');
    btn.dataset.nextUrl = (nextBtn && nextBtn.dataset.nextUrl) ? nextBtn.dataset.nextUrl : '';

    return !!btn.dataset.nextUrl;
  }

  // ── Pagination count display ───────────────────────────────────────────────

  function updatePaginationCount(btn) {
    var wrapper = btn.closest('.load-more-wrapper');
    if (!wrapper) return;
    var countText = wrapper.querySelector('.pagination-count__text');
    if (!countText) return;
    var shown = Math.min(
      (parseInt(btn.dataset.currentPage) || 1) * (parseInt(btn.dataset.pageSize) || 0),
      parseInt(btn.dataset.totalItems) || 0
    );
    countText.textContent = 'Viewing ' + shown + ' of ' + btn.dataset.totalItems + ' products';
    countText.dataset.productsShown = shown;
  }

  // ── Load More button ───────────────────────────────────────────────────────

  async function loadNextPage(btn) {
    var originalText = btn.textContent;
    btn.textContent = btn.getAttribute('data-loading-text') || 'Loading…';
    btn.disabled = true;

    try {
      var hasMore = await fetchAndAppend(btn);
      updatePaginationCount(btn);
      saveState(window.scrollY); // persist updated HTML for back-nav restoration

      // Update the URL with the current page as a query param
      var currentPage = parseInt(btn.dataset.currentPage) || 1;
      var url = new URL(window.location.href);
      url.searchParams.set('lm_page', currentPage);
      history.replaceState(null, '', url.toString());

      if (hasMore) {
        btn.textContent = originalText;
        btn.disabled = false;
      } else {
        var wrapper = btn.closest('.load-more-wrapper');
        var div = wrapper && wrapper.querySelector('.js-only');
        if (div) div.style.display = 'none';
      }
    } catch (e) {
      console.error('Load More failed:', e);
      btn.textContent = originalText;
      btn.disabled = false;
    }
  }

  function init() {
    var wrapper = document.querySelector('.load-more-wrapper');
    if (!wrapper) return;

    var loadMoreDiv = wrapper.querySelector('.js-only');
    var numberedNav = wrapper.querySelector('.pagination--numbered');
    var btn         = wrapper.querySelector('.load-more__button');
    var countDiv    = wrapper.querySelector('.pagination-count');

    if (!btn) return;
    if (!numberedNav || !numberedNav.classList.contains('pagination--hidden-by-js')) return;

    if (!btn.dataset.nextUrl) {
      if (loadMoreDiv) loadMoreDiv.style.display = 'none';
      numberedNav.style.display = 'none';
      return;
    }

    if (loadMoreDiv) loadMoreDiv.style.display = '';
    if (countDiv)    countDiv.style.display    = '';
    numberedNav.style.display = 'none';

    if (!btn.dataset.loadMoreInitialized) {
      btn.addEventListener('click', function () { loadNextPage(this); });
      btn.dataset.loadMoreInitialized = 'true';
    }
  }

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  function setup() {
    init();
    attachSaveListeners();
    // If state was restored above, sync the visible pagination count.
    var btn = document.querySelector('.load-more__button');
    if (btn && parseInt(btn.dataset.currentPage) > 1) updatePaginationCount(btn);
  }

  function observeGrid() {
    var container = document.querySelector('.product-grid-container, #ProductGridContainer');
    if (!container) return;
    new MutationObserver(init).observe(container, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setup();
      observeGrid();
    });
  } else {
    setup();
    observeGrid();
  }

  // ── Safari bfcache: pageshow fires when browser restores page from memory ───
  // DOMContentLoaded does NOT re-fire on bfcache restores, so we re-apply scroll here.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return; // not a bfcache restore — nothing to do
    var raw;
    try { raw = localStorage.getItem(stateKey()); } catch (_) { return; }
    if (!raw) return;
    var state;
    try { state = JSON.parse(raw); } catch (_) { return; }
    if (!state.scrollY) return;
    // DOM is already restored from cache — just re-scroll.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.scrollTo(0, state.scrollY);
      });
    });
  });

})();
